import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { ChevronDown, Lock, Search } from 'lucide-react';
import { useState } from 'react';
import { AccountDrawer } from '@/components/account-drawer';
import { Checkbox } from '@/components/form';
import { EmptyState, PageHeader } from '@/components/page-header';
import { toast } from '@/components/toast';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { useAccounts, type Account } from '@/lib/accounts';
import { api } from '@/lib/api';
import { useMe } from '@/lib/clients';
import { formatAmount } from '@/lib/format';
import { useHomeCurrency } from '@/lib/currencies';
import { useOrgId } from '@/lib/org';
import { useCompanySettings } from '@/lib/settings';
import { taxWord } from '@/lib/org-locale';

export const Route = createFileRoute('/accounting/chart-of-accounts/')({
  component: ChartOfAccounts,
});

const BALANCE_SHEET = new Set(['asset', 'liability', 'equity']);

/** Screen A1 (document 06): list pattern with the account drawer. */
function ChartOfAccounts() {
  const home = useHomeCurrency();
  const orgId = useOrgId();
  const me = useMe();
  const canEdit = me.data?.isStaff ?? false;
  const [includeInactive, setIncludeInactive] = useState(false);
  const [q, setQ] = useState('');
  const [drawer, setDrawer] = useState<{ open: boolean; account: Account | null }>({
    open: false,
    account: null,
  });
  const [confirm, setConfirm] = useState<Account | null>(null);
  const accounts = useAccounts({ includeInactive });
  const navigate = useNavigate();
  // Company settings › Advanced › Enable account numbers (QBO): hides the Number column
  const showNumbers = useCompanySettings().data?.advanced.enableAccountNumbers ?? true;

  const needle = q.trim().toLowerCase();
  const rows = (accounts.data ?? []).filter(
    (a) =>
      !needle ||
      a.fullName.toLowerCase().includes(needle) ||
      a.code.toLowerCase().startsWith(needle) ||
      a.detailTypeName.toLowerCase().includes(needle),
  );

  return (
    <>
      <PageHeader
        title="Chart of accounts"
        actions={
          <>
            <Button variant="outline" asChild>
              <Link to="/reports/$reportKey" params={{ reportKey: 'account-list' }}>
                Run report
              </Link>
            </Button>
            {canEdit ? (
              <>
                <Button variant="outline" asChild>
                  <Link to="/settings/import/$kind" params={{ kind: 'accounts' }}>
                    Import
                  </Link>
                </Button>
                <Button onClick={() => setDrawer({ open: true, account: null })}>New</Button>
              </>
            ) : null}
          </>
        }
      />
      {!orgId ? (
        <EmptyState
          title="Select a client"
          hint="Use the client switcher in the top bar to choose whose books to open."
        />
      ) : (
        <>
          <div className="mb-3 flex flex-wrap items-center gap-x-4 gap-y-2">
            <div className="relative w-full sm:w-[320px]">
              <Search className="pointer-events-none absolute left-2 top-2.5 size-4 text-muted-foreground" />
              <Input
                className="bg-card pl-8"
                placeholder="Filter by name or number"
                aria-label="Filter accounts"
                value={q}
                onChange={(e) => setQ(e.target.value)}
              />
            </div>
            <Checkbox
              label="Include inactive"
              checked={includeInactive}
              onChange={(e) => setIncludeInactive(e.target.checked)}
            />
            {accounts.data ? (
              <span className="ml-auto text-sm text-muted-foreground">{rows.length} accounts</span>
            ) : null}
          </div>
          {accounts.isPending ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : accounts.isError ? (
            <EmptyState title="Could not load accounts" hint={accounts.error.message} />
          ) : rows.length === 0 ? (
            <EmptyState title="No accounts match your filter" />
          ) : (
            <div className="rounded-lg border bg-card">
              <Table>
                <TableHeader className="sticky top-0 bg-card">
                  <TableRow>
                    {showNumbers ? <TableHead className="w-[90px]">Number</TableHead> : null}
                    <TableHead>Name</TableHead>
                    <TableHead>Account type</TableHead>
                    <TableHead>Detail type</TableHead>
                    <TableHead className="w-[70px]">{taxWord()}</TableHead>
                    <TableHead className="w-[150px] text-right">
                      {home ? `Balance (${home})` : 'Balance'}
                    </TableHead>
                    <TableHead className="w-[190px] text-right">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((a) => {
                    const isBs = BALANCE_SHEET.has(a.class);
                    const register = () =>
                      void navigate({
                        to: '/accounting/chart-of-accounts/$accountId/register',
                        params: { accountId: a.id },
                      });
                    // QBO: Run report opens the account's transactions (QuickReport)
                    const report = () =>
                      void navigate({
                        to: '/reports/$reportKey',
                        params: { reportKey: 'transaction-detail-by-account' },
                        search: { accounts: a.id },
                      });
                    return (
                      <TableRow key={a.id} className={a.isActive ? '' : 'text-muted-foreground'}>
                        {showNumbers ? <TableCell className="tnum">{a.code}</TableCell> : null}
                        <TableCell>
                          <span
                            className="inline-flex items-center gap-1.5"
                            style={{ paddingLeft: a.depth * 18 }}
                          >
                            {a.name}
                            {a.systemKey || a.isControl ? (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Lock
                                    className="size-3.5 text-muted-foreground"
                                    aria-label="System account"
                                  />
                                </TooltipTrigger>
                                <TooltipContent>
                                  {a.isControl
                                    ? 'Control account: posted to by documents, not journal entries.'
                                    : 'System account: cannot be made inactive.'}
                                </TooltipContent>
                              </Tooltip>
                            ) : null}
                            {!a.isActive ? <Badge variant="outline">Inactive</Badge> : null}
                          </span>
                        </TableCell>
                        <TableCell>{a.accountTypeName}</TableCell>
                        <TableCell>{a.detailTypeName}</TableCell>
                        <TableCell>{a.defaultTaxCode ?? ''}</TableCell>
                        <TableCell className="tnum text-right">
                          {isBs ? formatAmount(a.balance) : ''}
                          {isBs && a.foreignBalance !== null ? (
                            <div className="text-xs text-muted-foreground">
                              {a.accountCurrency} {formatAmount(a.foreignBalance)}
                            </div>
                          ) : null}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="inline-flex">
                            <Button
                              size="sm"
                              variant="link"
                              className="px-2"
                              onClick={isBs ? register : report}
                            >
                              {isBs ? 'View register' : 'Run report'}
                            </Button>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button
                                  size="icon-sm"
                                  variant="ghost"
                                  aria-label={`More actions for ${a.name}`}
                                >
                                  <ChevronDown className="size-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                {canEdit ? (
                                  <DropdownMenuItem
                                    onSelect={() => setDrawer({ open: true, account: a })}
                                  >
                                    Edit
                                  </DropdownMenuItem>
                                ) : null}
                                {canEdit && a.isActive ? (
                                  <DropdownMenuItem
                                    disabled={Boolean(a.systemKey || a.isControl)}
                                    onSelect={() => setConfirm(a)}
                                  >
                                    Make inactive
                                  </DropdownMenuItem>
                                ) : null}
                                {canEdit && !a.isActive ? <ReactivateItem account={a} /> : null}
                                <DropdownMenuItem onSelect={isBs ? report : register}>
                                  {isBs ? 'Run report' : 'View register'}
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </>
      )}
      <AccountDrawer
        open={drawer.open}
        account={drawer.account}
        onOpenChange={(open) => setDrawer((d) => ({ ...d, open }))}
      />
      <MakeInactiveDialog account={confirm} onClose={() => setConfirm(null)} />
    </>
  );
}

function ReactivateItem({ account }: { account: Account }) {
  const orgId = useOrgId();
  const qc = useQueryClient();
  const m = useMutation({
    mutationFn: () =>
      api<Account>(`/orgs/${orgId}/accounts/${account.id}/reactivate`, { method: 'POST' }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['accounts', orgId] });
      toast(`${account.name} is active again.`);
    },
    onError: (e) => toast(e.message, { tone: 'error' }),
  });
  return <DropdownMenuItem onSelect={() => m.mutate()}>Make active</DropdownMenuItem>;
}

function MakeInactiveDialog({
  account,
  onClose,
}: {
  account: Account | null;
  onClose: () => void;
}) {
  const orgId = useOrgId();
  const qc = useQueryClient();
  const m = useMutation({
    mutationFn: (a: Account) =>
      api<Account>(`/orgs/${orgId}/accounts/${a.id}/deactivate`, { method: 'POST' }),
    onSuccess: async (a) => {
      await qc.invalidateQueries({ queryKey: ['accounts', orgId] });
      toast(`${a.name} is now inactive.`);
      onClose();
    },
  });
  return (
    <Dialog
      open={Boolean(account)}
      onOpenChange={(open) => {
        if (!open) {
          m.reset();
          onClose();
        }
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Make {account?.name} inactive?</DialogTitle>
          <DialogDescription>
            Inactive accounts are hidden from lists and cannot be used on new transactions. Their
            history stays in reports. Accounts with a balance must be cleared first.
          </DialogDescription>
        </DialogHeader>
        {m.error ? (
          <p role="alert" className="text-sm text-destructive">
            {m.error.message}
          </p>
        ) : null}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            disabled={m.isPending}
            onClick={() => account && m.mutate(account)}
          >
            Make inactive
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
